import { Injectable } from '@angular/core';
import { AngularFireFunctions } from '@angular/fire/compat/functions';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Observable, from, throwError } from 'rxjs';
import { map, catchError, switchMap } from 'rxjs/operators';

export interface ExternalLoginResponse {
  success: boolean;
  customToken: string;
  user: {
    uid: string;
    email: string;
    userName: string;
    role: string;
    permissions: any[];
    profilePhoto: string | null;
    centresAllowed: string[];
    department: string;
    jobtitle: string | null;
    mobile: string | null;
    location: string | null;
  };
  isNewUser: boolean;
  message: string;
}

export interface TokenVerificationResponse {
  success: boolean;
  uid: string;
  email: string;
  emailVerified: boolean;
  name: string | null;
  picture: string | null;
}

/**
 * Service for external authentication integration
 * Allows AccessLife users to authenticate into external systems like YourDrive
 */
@Injectable({
  providedIn: 'root'
})
export class ExternalAuthService {

  constructor(
    private functions: AngularFireFunctions,
    private auth: AngularFireAuth
  ) {}

  /**
   * Get current user's Firebase ID token
   * This token can be sent to external systems for authentication
   */
  async getCurrentUserToken(): Promise<string | null> {
    const user = await this.auth.currentUser;
    if (!user) {
      console.error('No user logged in');
      return null;
    }
    return await user.getIdToken();
  }

  /**
   * Verify a Firebase token
   * Useful for testing token validity before sending to external systems
   */
  verifyToken(firebaseToken: string): Observable<TokenVerificationResponse> {
    const callable = this.functions.httpsCallable('verifyFirebaseToken');
    return callable({ firebaseToken }).pipe(
      map((result: any) => result as TokenVerificationResponse),
      catchError(error => {
        console.error('Token verification failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Login to external system (like YourDrive) using Firebase token
   * This is the main function for cross-application authentication
   */
  loginToExternalSystem(
    device: string = 'AccessLife Admin',
    location?: { latitude: string; longitude: string }
  ): Observable<ExternalLoginResponse> {
    return from(this.getCurrentUserToken()).pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('User not authenticated'));
        }

        const callable = this.functions.httpsCallable('externalFirebaseLogin');
        return callable({
          firebaseToken: token,
          device: device,
          latitude: location?.latitude || null,
          longitude: location?.longitude || null
        });
      }),
      map((result: any) => result as ExternalLoginResponse),
      catchError(error => {
        console.error('External login failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get user profile by UID
   */
  getUserProfile(uid: string): Observable<any> {
    const callable = this.functions.httpsCallable('getUserProfile');
    return callable({ uid }).pipe(
      map((result: any) => result),
      catchError(error => {
        console.error('Get user profile failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Refresh custom token
   * Call this when the custom token expires
   */
  refreshCustomToken(): Observable<{ success: boolean; customToken: string; expiresIn: number }> {
    return from(this.getCurrentUserToken()).pipe(
      switchMap(token => {
        if (!token) {
          return throwError(() => new Error('User not authenticated'));
        }

        const callable = this.functions.httpsCallable('refreshCustomToken');
        return callable({ firebaseToken: token });
      }),
      map((result: any) => result),
      catchError(error => {
        console.error('Token refresh failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Generate YourDrive login URL with embedded token
   * Replace 'YOUR_YOURDRIVE_URL' with actual YourDrive URL
   */
  async generateYourDriveLoginUrl(yourDriveBaseUrl: string = 'https://yourdrive.example.com'): Promise<string | null> {
    try {
      const token = await this.getCurrentUserToken();
      if (!token) return null;

      // YourDrive will receive this token and verify it
      return `${yourDriveBaseUrl}/firebase-login?token=${encodeURIComponent(token)}`;
    } catch (error) {
      console.error('Failed to generate YourDrive URL:', error);
      return null;
    }
  }

  /**
   * Open YourDrive in new window with authentication
   */
  async openYourDriveWithAuth(yourDriveBaseUrl: string = 'https://yourdrive.example.com'): Promise<void> {
    const url = await this.generateYourDriveLoginUrl(yourDriveBaseUrl);
    if (url) {
      window.open(url, '_blank');
    } else {
      throw new Error('Failed to generate authentication URL');
    }
  }

  /**
   * Get authentication data for embedding in iframe
   * Returns the necessary data to authenticate in an embedded context
   */
  async getEmbedAuthData(): Promise<{
    firebaseToken: string;
    uid: string;
    email: string;
  } | null> {
    try {
      const user = await this.auth.currentUser;
      if (!user) return null;

      const token = await user.getIdToken();

      return {
        firebaseToken: token,
        uid: user.uid,
        email: user.email!
      };
    } catch (error) {
      console.error('Failed to get embed auth data:', error);
      return null;
    }
  }

  /**
   * Batch verify multiple tokens
   * Useful for admin operations
   */
  batchVerifyTokens(tokens: string[]): Observable<any> {
    const callable = this.functions.httpsCallable('batchVerifyTokens');
    return callable({ tokens }).pipe(
      map((result: any) => result),
      catchError(error => {
        console.error('Batch verification failed:', error);
        return throwError(() => error);
      })
    );
  }
}

